package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AgeGroup
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.accountName
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement

/** `/app/me/settings` — 프로필(이름·연령대) 요약, 로그아웃, 계정 삭제 링크(가이드 §3.7). */
object MySettingsPage {
    private val scope = MainScope()
    private val accountRepository = SharedGraph.createAccountRepository()

    fun render(session: SessionState, profileSuccessMessage: String? = null) {
        scope.launch {
            val context = loadMemberContext(session)
            MemberShell.render(context, MemberShell.Tab.SETTINGS, body(context, profileSuccessMessage))
            wireLogout()
            wireProfile(profileSuccessMessage)
        }
    }

    private fun body(context: MemberContext, profileSuccessMessage: String?): String {
        val account = context.session.account
        val ageLabel = when (account?.ageGroup) {
            AgeGroup.UNDER_14 -> "만 14세 미만"
            AgeGroup.OVER_14 -> "만 14세 이상"
            else -> "미입력"
        }
        return """
            <h1 class="eb-page-title">설정</h1>
            <section class="eb-card">
                <h2>내 정보</h2>
                <form id="settings-profile-form" novalidate>
                    <div class="eb-field">
                        <label for="settings-profile-name">이름</label>
                        <input id="settings-profile-name" maxlength="30" required value="${context.session.accountName().escapeHtml()}">
                        <p class="eb-meta">학원 회원 관리와 알림에 표시되는 이름이에요.</p>
                    </div>
                    <p class="eb-form-error" id="settings-profile-error" role="alert" hidden></p>
                    <p class="eb-meta eb-text-success" id="settings-profile-success" role="status"${if (profileSuccessMessage == null) " hidden" else ""}></p>
                    <button class="eb-button eb-button--primary" id="settings-profile-submit" type="submit">이름 저장</button>
                </form>
                <p class="eb-meta">${account?.email?.escapeHtml().orEmpty()}</p>
                <p class="eb-meta">연령대: $ageLabel</p>
            </section>
            <section class="eb-card">
                <button class="eb-button eb-button--ghost eb-button--block" id="settings-logout-button">${icon("icon-logout", "sm")}로그아웃</button>
            </section>
            <a class="eb-button eb-button--link" href="/app/me/account-deletion">계정 삭제</a>
        """.trimIndent()
    }

    private fun wireLogout() {
        val button = document.getElementById("settings-logout-button") as? HTMLButtonElement ?: return
        button.onclick = {
            scope.launch { accountRepository.logout() }
            null
        }
    }

    private fun wireProfile(profileSuccessMessage: String?) {
        val form = document.getElementById("settings-profile-form") as? HTMLFormElement ?: return
        val success = document.getElementById("settings-profile-success")
        success?.textContent = profileSuccessMessage
        form.onsubmit = { event ->
            event.preventDefault()
            val name = (document.getElementById("settings-profile-name") as? HTMLInputElement)?.value?.trim().orEmpty()
            if (name.length !in 1..30) {
                showProfileError("이름은 1~30자로 입력해 주세요.")
            } else {
                val submitButton = document.getElementById("settings-profile-submit") as? HTMLButtonElement
                submitButton?.disabled = true
                scope.launch {
                    val result = accountRepository.updateMyProfile(name)
                    if (result.isSuccess) {
                        render(accountRepository.currentSession(), "이름을 저장했어요.")
                    } else {
                        submitButton?.disabled = false
                        showProfileError(result.errorMessage ?: "이름을 저장하지 못했어요. 잠시 후 다시 시도해 주세요.")
                    }
                }
            }
            null
        }
    }

    private fun showProfileError(message: String) {
        val error = document.getElementById("settings-profile-error") ?: return
        error.textContent = message
        error.removeAttribute("hidden")
    }
}
